/**
 * Phase 27.37.B — Contact SMS Card (Lead + Prospect).
 *
 * Renders the SMS conversation thread + composer + consent panel for
 * a specific lead or prospect. Works in three modes:
 *
 *  1. Twilio not configured yet → composer shows a warning banner,
 *     but the message can still be queued (backend soft-fails).
 *  2. Contact `opted_out` → composer is disabled + explanation.
 *  3. Contact `opted_in` → normal composer with template picker +
 *     live segment counter.
 *
 * Kept SMS-body OUT of the CRM Activity feed by design — the drawer
 * is the single source of truth for message content.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  MessageSquare, Send, Loader2, ShieldCheck, ShieldOff,
  AlertTriangle, CheckCircle2, XCircle, FileText, RefreshCw, Sparkles, Phone,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { smsMessagingAPI, smsCopilotAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

const STATUS_META = {
  queued: { label: 'Queued', tone: 'bg-slate-100 text-slate-700 border-slate-300' },
  accepted: { label: 'Accepted', tone: 'bg-slate-100 text-slate-700 border-slate-300' },
  sent: { label: 'Sent', tone: 'bg-sky-100 text-sky-800 border-sky-300' },
  delivered: { label: 'Delivered', tone: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  read: { label: 'Read', tone: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  undelivered: { label: 'Undelivered', tone: 'bg-amber-100 text-amber-900 border-amber-300' },
  failed: { label: 'Failed', tone: 'bg-rose-100 text-rose-800 border-rose-300' },
  received: { label: 'Received', tone: 'bg-purple-100 text-purple-800 border-purple-300' },
};

export default function ContactSmsCard({ contactType, contactId, phone }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [body, setBody] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [templates, setTemplates] = useState([]);
  const [segments, setSegments] = useState({ chars: 0, segments: 0, encoding: 'GSM-7' });
  const [sending, setSending] = useState(false);
  const [senderPool, setSenderPool] = useState({ senders: [], source: 'none', sticky_sender_active: false });
  const [fromNumber, setFromNumber] = useState('');
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [consentWording, setConsentWording] = useState(null);
  const [showOptOutDialog, setShowOptOutDialog] = useState(false);
  const [optOutReason, setOptOutReason] = useState('');

  // Phase 27.37.J — AI Copilot state.
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [copilotLoading, setCopilotLoading] = useState(false);
  const [copilotHint, setCopilotHint] = useState('');
  const [copilotSuggestions, setCopilotSuggestions] = useState([]);
  const [copilotSuggestionId, setCopilotSuggestionId] = useState(null);
  const [copilotOriginalText, setCopilotOriginalText] = useState('');

  // Phase 27.37.N — Per-agent Lead SMS permission gate. KR admins are
  // always allowed; non-KR agents only when `can_sms_leads=true` on
  // their agent record. Prospects/agent_user threads are unaffected.
  const [permissions, setPermissions] = useState({ can_sms_leads: true, is_kr_admin: false });
  const leadSendBlocked = contactType === 'lead' && !permissions.can_sms_leads;

  // Phase 27.37.O — First-touch welcome opener for new leads.
  // Shown ONLY when: contact is a Lead, has phone, opted_in, permission
  // OK, AND there are zero outbound messages yet. Auto-fetches once
  // per open of the drawer. Agent taps a suggestion to seed the composer.
  const [welcome, setWelcome] = useState({ loading: false, tried: false, suggestions: [], suggestion_id: null });

  const threadRef = useRef(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const r = await smsMessagingAPI.conversation(contactType, contactId);
      setData((prev) => {
        // Preserve scroll position when the message count didn't change
        // on a silent refresh — most polls will be no-ops, no need to
        // trigger the "scroll to bottom" effect.
        if (silent && prev?.messages?.length === r.data?.messages?.length) {
          return { ...r.data, messages: r.data.messages };
        }
        return r.data;
      });
    } catch (e) {
      if (!silent) setError(getErrorMessage(e, 'Failed to load SMS conversation'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [contactType, contactId]);

  const loadTemplates = useCallback(async () => {
    try {
      const lang = data?.contact?.preferred_language || 'en';
      const r = await smsMessagingAPI.templates({ active_only: true, language: lang });
      setTemplates(r.data.templates || []);
    } catch (_) { /* templates are optional */ }
  }, [data?.contact?.preferred_language]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (data) loadTemplates(); }, [data, loadTemplates]);

  // Phase 27.38.6 — Auto-refresh the thread so inbound messages appear
  // without the agent having to hit a refresh button.
  //   • Poll every 15 s while the tab is visible AND the card is
  //     mounted. Skips when hidden to save the server.
  //   • Fires an immediate refresh when the tab becomes visible again
  //     so returning from another tab always shows the latest.
  //   • Uses a ref to the loader so the interval doesn't re-init on
  //     every render (which would restart the clock unpredictably).
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);
  useEffect(() => {
    if (!contactId) return undefined;
    // Phase 27.57 — presence heartbeat: tell the backend this operator
    // is actively viewing the conversation so inbound-message
    // notifications are suppressed for them while the thread is open.
    const ping = () => {
      if (document.visibilityState === 'visible') {
        smsMessagingAPI.presence(contactType, contactId).catch(() => {});
      }
    };
    ping();
    const tick = () => {
      if (document.visibilityState === 'visible') {
        loadRef.current?.({ silent: true });
        ping();
      }
    };
    const interval = setInterval(tick, 15000);
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        loadRef.current?.({ silent: true });
        ping();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [contactId, contactType]);

  // Load the sender pool once — populates the "Send from" dropdown.
  useEffect(() => {
    (async () => {
      try {
        const r = await smsMessagingAPI.senders();
        setSenderPool(r.data || { senders: [], source: 'none', sticky_sender_active: false });
      } catch (_) { /* senders are optional; sticky-sender takes over */ }
    })();
  }, []);

  // Phase 27.37.N — Load per-agent SMS permissions once. Only the
  // Lead thread cares about the flag today, but we cache it so future
  // gates (broadcasts, automations) can reuse the same call.
  useEffect(() => {
    (async () => {
      try {
        const r = await smsMessagingAPI.permissions();
        setPermissions(r.data || { can_sms_leads: false, is_kr_admin: false });
      } catch (_) { /* fall back to permissive; backend still enforces */ }
    })();
  }, []);

  // Live segment counter — debounced.
  useEffect(() => {
    if (!body) { setSegments({ chars: 0, segments: 0, encoding: 'GSM-7' }); return; }
    const t = setTimeout(async () => {
      try {
        const r = await smsMessagingAPI.segments(body);
        setSegments(r.data);
      } catch (_) { /* segment preview is cosmetic */ }
    }, 220);
    return () => clearTimeout(t);
  }, [body]);

  // Auto-scroll thread to bottom on messages update.
  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [data?.messages?.length]);

  const consent = data?.consent || { status: 'not_opted_in' };
  const optedOut = consent.status === 'opted_out';
  const optedIn = consent.status === 'opted_in';
  const messages = data?.messages || [];
  const hasNoOutbound = !messages.some((m) => m.direction === 'outbound');
  const shouldOfferWelcome =
    contactType === 'lead' && !!phone && optedIn && !leadSendBlocked &&
    hasNoOutbound && messages.length === 0;

  // Phase 27.37.O — Fetch welcome suggestions when eligible. Runs once
  // per drawer open (guarded by `tried`) so we don't burn credits when
  // the agent scrolls or refreshes the thread.
  useEffect(() => {
    if (!shouldOfferWelcome || welcome.tried || welcome.loading) return;
    let cancelled = false;
    (async () => {
      setWelcome((w) => ({ ...w, loading: true }));
      try {
        const r = await smsCopilotAPI.welcome({ lead_id: contactId });
        if (cancelled) return;
        setWelcome({
          loading: false, tried: true,
          suggestions: r.data?.suggestions || [],
          suggestion_id: r.data?.suggestion_id || null,
        });
      } catch (_) {
        if (!cancelled) setWelcome({ loading: false, tried: true, suggestions: [], suggestion_id: null });
      }
    })();
    return () => { cancelled = true; };
  }, [shouldOfferWelcome, welcome.tried, welcome.loading, contactId]);

  const pickWelcome = (text) => {
    setBody(text);
    setCopilotOriginalText(text);
    setCopilotSuggestionId(welcome.suggestion_id);
    toast.success('Bienvenida aplicada — revisa y envía');
  };

  // Detect the "Twilio credentials are not loaded yet" state so we
  // can surface a top-level banner in the composer — cheaper than an
  // extra API call and only KR admins have access to the config route.
  const providerNotConfigured = messages.some(
    (m) => m.direction === 'outbound' && m.provider_error_code === 'not_configured',
  );

  const openConsentDialog = async () => {
    setShowConsentDialog(true);
    if (!consentWording) {
      try {
        const lang = data?.contact?.preferred_language || 'en';
        const r = await smsMessagingAPI.consentLanguage(lang);
        setConsentWording(r.data);
      } catch (e) { setConsentWording({ wording: 'Unavailable' }); }
    }
  };

  const confirmOptIn = async () => {
    try {
      await smsMessagingAPI.optIn({ contact_type: contactType, contact_id: contactId });
      toast.success('Consent recorded — SMS enabled');
      setShowConsentDialog(false);
      await load();
    } catch (e) { toast.error(getErrorMessage(e, 'Opt-in failed')); }
  };

  const confirmOptOut = async () => {
    try {
      await smsMessagingAPI.optOut({
        contact_type: contactType, contact_id: contactId,
        reason: optOutReason.trim() || undefined,
      });
      toast.success('Contact opted out — SMS disabled');
      setShowOptOutDialog(false); setOptOutReason('');
      await load();
    } catch (e) { toast.error(getErrorMessage(e, 'Opt-out failed')); }
  };

  const applyTemplate = (id) => {
    setTemplateId(id);
    if (!id) return;
    const tpl = templates.find((t) => t.template_id === id);
    if (tpl) setBody(tpl.body);
  };

  const sendMessage = async () => {
    if (!body.trim()) { toast.error('Message body is empty'); return; }
    setSending(true);
    try {
      await smsMessagingAPI.send({
        contact_type: contactType, contact_id: contactId,
        body, template_id: templateId || undefined,
        from_number: fromNumber || undefined,
      });
      // Phase 27.37.J — close the AI audit loop when this send came
      // from a Copilot suggestion. Records whether the agent used it
      // verbatim (edited=false) or edited it (edited=true) so
      // compliance can review LLM performance later.
      if (copilotSuggestionId) {
        try {
          await smsCopilotAPI.markUsed({
            suggestion_id: copilotSuggestionId,
            chosen_index: copilotSuggestions.findIndex((s) => s === copilotOriginalText),
            edited: body.trim() !== copilotOriginalText.trim(),
            final_body: body,
          });
        } catch (_) { /* audit is best-effort */ }
      }
      setBody(''); setTemplateId('');
      setCopilotSuggestionId(null); setCopilotOriginalText('');
      await load();
      toast.success('Message queued');
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      const reason = e?.response?.data?.detail?.reason;
      if (code === 'opted_out') { toast.error(reason); await load(); }
      else toast.error(reason || getErrorMessage(e, 'Send failed'));
    } finally { setSending(false); }
  };

  // Phase 27.37.J — AI Copilot: fetch 3 reply suggestions.
  const openCopilot = () => { setCopilotHint(''); setCopilotSuggestions([]); setCopilotOpen(true); };
  const generateSuggestions = async () => {
    setCopilotLoading(true);
    try {
      const r = await smsCopilotAPI.suggest({
        contact_type: contactType,
        contact_id: contactId,
        agent_hint: copilotHint || null,
      });
      setCopilotSuggestions(r.data?.suggestions || []);
      setCopilotSuggestionId(r.data?.suggestion_id || null);
    } catch (e) {
      toast.error(getErrorMessage(e, 'AI Copilot unavailable — try again shortly'));
    } finally { setCopilotLoading(false); }
  };
  const pickSuggestion = (text) => {
    setBody(text);
    setCopilotOriginalText(text);
    setCopilotOpen(false);
    toast.success('Suggestion applied — edit before sending if needed');
  };

  if (loading) {
    return (
      <Card><CardContent className="p-6 flex items-center gap-2 justify-center text-slate-500">
        <Loader2 className="animate-spin" size={16} /> Loading messages…
      </CardContent></Card>
    );
  }
  if (error) {
    return (
      <Card><CardContent className="p-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded">
        {error}
      </CardContent></Card>
    );
  }

  return (
    <Card data-testid="contact-sms-card">
      <CardHeader className="flex flex-row items-center justify-between py-3">
        <CardTitle className="text-base flex items-center gap-2">
          <MessageSquare size={16} className="text-sky-600" />
          Messages / SMS
          <Badge variant="outline" className="ml-1 font-normal text-[10px]">
            {messages.length}
          </Badge>
        </CardTitle>
        <div className="flex items-center gap-2">
          {phone && (
            <Button
              variant="ghost" size="sm"
              onClick={() => {
                if (window.krSoftphoneDial) window.krSoftphoneDial(phone, { contact_type: contactType, contact_id: contactId });
                else toast.error('Softphone unavailable');
              }}
              className="text-emerald-700 hover:bg-emerald-50"
              data-testid="sms-call-btn"
              title="Call via softphone"
            >
              <Phone size={13} className="mr-1" /> Call
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={load} data-testid="sms-refresh"><RefreshCw size={13} /></Button>
          <ConsentPill consent={consent} />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {!phone && (
          <div className="p-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-center gap-2">
            <AlertTriangle size={12} /> Contact has no phone number — SMS unavailable.
          </div>
        )}

        {/* Consent management */}
        {phone && !optedIn && !optedOut && (
          <div className="p-2 rounded bg-sky-50 border border-sky-200 text-xs text-sky-900 flex items-start gap-2" data-testid="consent-pending">
            <ShieldCheck size={13} className="mt-0.5" />
            <div className="flex-1">
              SMS consent has not been recorded yet. Confirm the contact agreed to
              receive text messages before sending.
              <div className="mt-1">
                <Button size="sm" onClick={openConsentDialog} data-testid="record-opt-in-btn">
                  Record opt-in
                </Button>
              </div>
            </div>
          </div>
        )}
        {phone && optedIn && (
          <div className="p-2 rounded bg-emerald-50 border border-emerald-200 text-xs text-emerald-900 flex items-center justify-between" data-testid="consent-opted-in">
            <span className="flex items-center gap-1.5"><ShieldCheck size={13} /> Opted in on {(consent.consent_at || '').slice(0, 10)}</span>
            <button onClick={() => setShowOptOutDialog(true)} className="text-[11px] underline hover:text-emerald-950" data-testid="record-opt-out-btn">
              Record opt-out
            </button>
          </div>
        )}
        {phone && optedOut && (
          <div className="p-2 rounded bg-rose-50 border border-rose-200 text-xs text-rose-900 flex items-center justify-between" data-testid="consent-opted-out">
            <span className="flex items-center gap-1.5"><ShieldOff size={13} /> Opted out on {(consent.opt_out_at || '').slice(0, 10)} — SMS disabled</span>
            <button onClick={openConsentDialog} className="text-[11px] underline hover:text-rose-950" data-testid="reactivate-opt-in-btn">
              Re-enable (new opt-in)
            </button>
          </div>
        )}

        {/* Thread */}
        <div
          ref={threadRef}
          className="max-h-80 overflow-y-auto p-2 bg-slate-50 rounded border border-slate-200 space-y-2"
          data-testid="sms-thread"
        >
          {messages.length === 0 ? (
            <div className="text-center py-8 text-xs text-slate-500 italic">No messages yet.</div>
          ) : (() => {
            // Phase 27.53.M — Inject a subtle date separator whenever the
            // day changes so long threads are scannable.
            const out = [];
            let lastDay = null;
            const dayKey = (iso) => {
              if (!iso) return null;
              const d = new Date(iso);
              return isNaN(d.getTime()) ? null : `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
            };
            for (const m of messages) {
              const dk = dayKey(m.created_at);
              if (dk && dk !== lastDay) {
                out.push(
                  <div key={`sep-${dk}-${m.message_id}`} className="flex items-center gap-2 my-2" data-testid={`sms-day-sep-${dk}`}>
                    <div className="flex-1 h-px bg-slate-200" />
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 px-2">
                      {formatDaySeparator(m.created_at)}
                    </span>
                    <div className="flex-1 h-px bg-slate-200" />
                  </div>
                );
                lastDay = dk;
              }
              out.push(<MessageBubble key={m.message_id} m={m} />);
            }
            return out;
          })()}
        </div>

        {/* Phase 27.37.O — AI Welcome opener for brand-new leads. */}
        {shouldOfferWelcome && (welcome.loading || welcome.suggestions.length > 0) && (
          <div
            className="p-3 rounded-lg border border-violet-200 bg-gradient-to-br from-violet-50 to-white"
            data-testid="sms-welcome-suggestions"
          >
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={14} className="text-violet-600" />
              <span className="text-xs font-semibold text-violet-900">
                Bienvenida sugerida por AI
              </span>
              <span className="text-[10px] text-violet-500 ml-auto">
                Toca una opción para usarla — puedes editarla antes de enviar
              </span>
            </div>
            {welcome.loading ? (
              <div className="flex items-center gap-2 text-xs text-violet-700 py-2">
                <Loader2 size={12} className="animate-spin" /> Generando sugerencias personalizadas…
              </div>
            ) : (
              <div className="space-y-1.5">
                {welcome.suggestions.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => pickWelcome(s)}
                    disabled={optedOut || leadSendBlocked}
                    data-testid={`sms-welcome-suggestion-${i}`}
                    className="w-full text-left text-xs leading-relaxed px-3 py-2 rounded-md bg-white border border-violet-200 hover:border-violet-400 hover:bg-violet-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="text-violet-700 font-semibold mr-1">#{i + 1}</span>
                    <span className="text-slate-800">{s}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Composer */}
        <div className="space-y-2">
          {providerNotConfigured && (
            <div
              className="p-2 rounded bg-amber-50 border border-amber-200 text-[11px] text-amber-900 flex items-center gap-2"
              data-testid="sms-provider-not-configured-banner"
            >
              <AlertTriangle size={12} />
              Twilio credentials aren&apos;t loaded yet. Messages are saved as{' '}
              <span className="font-semibold">Queued</span> and will dispatch as
              soon as the SMS integration is connected.
            </div>
          )}
          {leadSendBlocked && (
            <div
              className="p-2 rounded bg-slate-50 border border-slate-200 text-[11px] text-slate-700 flex items-center gap-2"
              data-testid="sms-lead-permission-banner"
            >
              <ShieldOff size={12} className="text-slate-500" />
              Sin permiso para enviar SMS a Leads. Solicita a un KR Admin que
              habilite esta opción desde tu perfil de Agente.
            </div>
          )}
          {templates.length > 0 && (
            <div className="flex items-center gap-2">
              <FileText size={13} className="text-slate-500" />
              <Select value={templateId} onValueChange={applyTemplate} disabled={optedOut || leadSendBlocked}>
                <SelectTrigger className="h-8 text-xs" data-testid="sms-template-picker">
                  <SelectValue placeholder="Use a template…" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.template_id} value={t.template_id}>
                      [{t.category}] {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {senderPool.senders.length > 1 && (
            <div className="flex items-center gap-2" data-testid="sms-sender-selector-row">
              <span className="text-[11px] text-slate-500 shrink-0">Send from</span>
              <Select
                value={fromNumber || '__auto__'}
                onValueChange={(v) => setFromNumber(v === '__auto__' ? '' : v)}
                disabled={optedOut || leadSendBlocked}
              >
                <SelectTrigger className="h-8 text-xs" data-testid="sms-sender-selector">
                  <SelectValue placeholder="Auto (Sticky Sender)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__auto__">Auto — Sticky Sender</SelectItem>
                  {senderPool.senders.map((s) => (
                    <SelectItem key={s.phone_number} value={s.phone_number}>
                      {s.label}{s.iso_country ? ` (${s.iso_country})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {senderPool.senders.length === 1 && (
            <div className="flex items-center gap-2 text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5"
                 data-testid="sms-single-sender">
              <span className="text-slate-500 shrink-0">Sending from</span>
              <span className="font-semibold text-slate-700">{senderPool.senders[0].label}</span>
            </div>
          )}
          <Textarea
            rows={3}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={
              optedOut ? 'Contact has opted out — SMS disabled.'
              : leadSendBlocked ? 'Sin permiso para enviar SMS a Leads.'
              : 'Type your message…'
            }
            disabled={optedOut || leadSendBlocked}
            data-testid="sms-composer-textarea"
          />
          <div className="flex items-center justify-between text-[11px] text-slate-500">
            <div data-testid="sms-segment-counter">
              {segments.chars} chars · {segments.segments} segment{segments.segments === 1 ? '' : 's'} ·{' '}
              <span className={segments.encoding === 'UCS-2' ? 'text-amber-600 font-medium' : ''}>{segments.encoding}</span>
              {segments.segments > 1 && <span className="text-amber-600 font-medium ml-1">· multi-segment SMS</span>}
            </div>
            <div className="flex items-center gap-2">
              {(contactType === 'lead' || contactType === 'prospect') && (
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button
                          size="sm" variant="outline"
                          onClick={openCopilot}
                          disabled={optedOut || !phone || leadSendBlocked}
                          data-testid="sms-ai-copilot-btn"
                          className="border-violet-300 text-violet-700 hover:bg-violet-50"
                        >
                          <Sparkles size={13} className="mr-1" /> AI Suggest
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {leadSendBlocked && (
                      <TooltipContent side="top">
                        Sin permiso para enviar SMS a Leads
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              )}
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        size="sm"
                        onClick={sendMessage}
                        disabled={sending || !body.trim() || optedOut || !phone || leadSendBlocked}
                        data-testid="sms-send-btn"
                      >
                        {sending ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Send size={13} className="mr-1" />}
                        Send
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {leadSendBlocked && (
                    <TooltipContent side="top">
                      Sin permiso para enviar SMS a Leads
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </div>
      </CardContent>

      {/* Consent dialog */}
      <Dialog open={showConsentDialog} onOpenChange={setShowConsentDialog}>
        <DialogContent className="max-w-lg" data-testid="consent-dialog">
          <DialogHeader>
            <DialogTitle>Record SMS opt-in</DialogTitle>
            <DialogDescription>
              Confirm the contact has agreed to receive text messages. This wording
              will be stored with a timestamp for audit purposes.
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs text-slate-700 whitespace-pre-wrap p-3 rounded bg-slate-50 border border-slate-200 max-h-56 overflow-y-auto">
            {consentWording?.wording || 'Loading…'}
          </div>
          <div className="text-[10px] text-slate-400">
            Version: {consentWording?.version} · Language: {consentWording?.language}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConsentDialog(false)}>Cancel</Button>
            <Button onClick={confirmOptIn} data-testid="consent-dialog-confirm">
              I confirm the contact agreed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Opt-out dialog */}
      <Dialog open={showOptOutDialog} onOpenChange={setShowOptOutDialog}>
        <DialogContent className="max-w-md" data-testid="opt-out-dialog">
          <DialogHeader>
            <DialogTitle>Record SMS opt-out</DialogTitle>
            <DialogDescription>
              Immediately disables outbound SMS for this contact. Optional reason
              is stored for audit history.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={optOutReason}
            onChange={(e) => setOptOutReason(e.target.value)}
            placeholder="Reason (optional)"
            rows={2}
            data-testid="opt-out-reason"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOptOutDialog(false)}>Cancel</Button>
            <Button className="bg-rose-500 hover:bg-rose-600" onClick={confirmOptOut} data-testid="opt-out-confirm">
              Record opt-out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 27.37.J — AI Copilot dialog */}
      <Dialog open={copilotOpen} onOpenChange={setCopilotOpen}>
        <DialogContent className="max-w-xl" data-testid="sms-ai-copilot-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles size={16} className="text-violet-600" />
              AI Reply Suggestions
            </DialogTitle>
            <DialogDescription>
              The copilot reads the last 20 messages, the lead&apos;s CRM notes and
              product interest, and drafts 3 short replies in the same
              language the lead used. You are always the sender — pick one,
              edit it, and hit Send when it reads the way you want.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600">
                Optional steer (what should the reply do?)
              </label>
              <Input
                value={copilotHint}
                onChange={(e) => setCopilotHint(e.target.value)}
                placeholder="e.g. ask when a good time to call is"
                maxLength={200}
                data-testid="sms-ai-hint-input"
              />
            </div>
            <Button
              onClick={generateSuggestions}
              disabled={copilotLoading}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="sms-ai-generate-btn"
            >
              {copilotLoading
                ? <Loader2 size={13} className="mr-2 animate-spin" />
                : <Sparkles size={13} className="mr-2" />}
              {copilotSuggestions.length ? 'Regenerate' : 'Generate suggestions'}
            </Button>
            {copilotSuggestions.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-slate-100">
                <div className="text-xs font-medium text-slate-600">
                  Click a suggestion to load it into the composer:
                </div>
                {copilotSuggestions.map((s, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => pickSuggestion(s)}
                    className="w-full text-left p-3 rounded-lg border border-slate-200 bg-white hover:border-violet-400 hover:bg-violet-50 transition-colors text-sm text-slate-800"
                    data-testid={`sms-ai-suggestion-${idx}`}
                  >
                    {s}
                  </button>
                ))}
                <div className="text-[10px] text-slate-400 pt-1">
                  Every generation is logged with the model, prompt context, and outcome for compliance review.
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopilotOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function ConsentPill({ consent }) {
  const status = consent?.status || 'not_opted_in';
  const map = {
    opted_in: { icon: CheckCircle2, tone: 'bg-emerald-100 text-emerald-800 border-emerald-300', label: 'Opted In' },
    opted_out: { icon: XCircle, tone: 'bg-rose-100 text-rose-800 border-rose-300', label: 'Opted Out' },
    not_opted_in: { icon: ShieldCheck, tone: 'bg-slate-100 text-slate-700 border-slate-300', label: 'Not opted in' },
  };
  const { icon: Icon, tone, label } = map[status] || map.not_opted_in;
  return (
    <Badge variant="outline" className={`text-[10px] font-normal ${tone}`} data-testid="consent-pill">
      <Icon size={10} className="mr-1" /> {label}
    </Badge>
  );
}

function formatMessageTimestamp(iso) {
  // Phase 27.53.M — On agent/lead profiles the SMS thread only showed
  // "3:45 PM" for every message which made it impossible to tell whether
  // a reply happened today or three weeks ago. This helper adds a smart
  // date prefix (Today / Yesterday / weekday / date) so tracking is easy
  // without cluttering fresh conversations.
  if (!iso) return { label: '', title: '' };
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { label: '', title: '' };
  const now = new Date();
  const timeStr = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const dayDiff = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);
  const fullTitle = d.toLocaleString([], {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
  let label;
  if (dayDiff === 0) label = timeStr;
  else if (dayDiff === 1) label = `Yesterday · ${timeStr}`;
  else if (dayDiff > 1 && dayDiff < 7) label = `${d.toLocaleDateString([], { weekday: 'short' })} · ${timeStr}`;
  else if (d.getFullYear() === now.getFullYear()) label = `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} · ${timeStr}`;
  else label = `${d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })} · ${timeStr}`;
  return { label, title: fullTitle };
}

function formatDaySeparator(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const dayDiff = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);
  if (dayDiff === 0) return 'Today';
  if (dayDiff === 1) return 'Yesterday';
  if (dayDiff < 7) return d.toLocaleDateString([], { weekday: 'long' });
  if (d.getFullYear() === now.getFullYear()) {
    return d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

function MessageBubble({ m }) {
  const isOut = m.direction === 'outbound';
  const meta = STATUS_META[m.status] || STATUS_META.queued;
  const { label: time, title: fullTs } = formatMessageTimestamp(m.created_at);
  return (
    <div className={`flex ${isOut ? 'justify-end' : 'justify-start'}`} data-testid={`sms-bubble-${m.message_id}`}>
      <div className={`max-w-[75%] rounded-2xl px-3 py-2 shadow-sm ${
        isOut ? 'bg-sky-500 text-white' : 'bg-white text-slate-800 border border-slate-200'
      }`}>
        <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>
        <div className={`text-[10px] mt-1 flex items-center gap-1 ${isOut ? 'text-sky-100' : 'text-slate-500'}`}>
          <span title={fullTs} data-testid={`sms-bubble-timestamp-${m.message_id}`}>{time}</span>
          <span className="opacity-70">·</span>
          <span className={`px-1.5 rounded ${isOut ? 'bg-white/20' : ''}`}>{meta.label}</span>
          {m.num_segments > 1 && <span className="opacity-80">· {m.num_segments} seg</span>}
          {m.provider_error_code && (
            <span className="ml-1 text-[9px] opacity-90" title={m.provider_error_message}>
              [{m.provider_error_code}]
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
